import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiMessageSquare, FiX } from 'react-icons/fi';
import { AssistantPanel, DEFAULT_LABELS } from '@vikar-experten/ask-ai-embedded/react';
import { administrationLabels } from '@vikar-experten/ask-ai-embedded/administration';
import aiService from '../../services/api/aiService';

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: var(--app-radius);
  font-size: var(--text-xs, 0.8rem);
  font-weight: 500;
  padding: 6px 12px;
  min-height: 40px;
  cursor: pointer;
  border: 1px solid ${({ $inverse }) => ($inverse ? 'rgba(255, 255, 255, 0.7)' : 'var(--act-primary, #17627f)')};
  background: transparent;
  color: ${({ $inverse }) => ($inverse ? 'var(--text-inverse, #fff)' : 'var(--act-primary, #17627f)')};
  &:hover { background: ${({ $inverse }) => ($inverse ? 'rgba(255, 255, 255, 0.12)' : 'rgba(23, 98, 127, 0.08)')}; }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
`;
const slideIn = keyframes`
  from { transform: translateX(24px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(25, 36, 46, 0.35);
  z-index: 1200;
`;
const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: min(480px, 100vw);
  display: flex;
  flex-direction: column;
  background: var(--app-bg, #f5f6f7);
  border-left: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: -8px 0 24px rgba(25, 36, 46, 0.18);
  z-index: 1201;
  animation: ${slideIn} 0.18s ease-out;
  &[hidden] { display: none; }
`;
const DrawerHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  background: var(--app-sidebar, #19242e);
  color: var(--text-inverse, #fff);
`;
const DrawerTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  font-size: var(--text-sm, 0.9rem);
`;
const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  min-width: 36px;
  height: 36px;
  padding: 0;
  flex: 0 0 36px;
  border: none;
  border-radius: var(--app-radius, 6px);
  background: transparent;
  color: var(--text-inverse, #fff);
  cursor: pointer;
  & svg { width: 18px; height: 18px; flex: none; }
  &:hover { background: rgba(255, 255, 255, 0.12); }
  &:focus-visible { outline: 2px solid currentColor; }
`;

/** Tempus supplies its authenticated API and drawer; the package owns the assistant. */
const AskAiAssistant = ({ inverse = false }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const triggerRef = useRef(null);
  const drawerRef = useRef(null);
  const labels = Object.fromEntries(Object.entries(DEFAULT_LABELS).map(([key, fallback]) => [key, t(`assistant.${key}`, { defaultValue: fallback })]));

  const close = () => {
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  };
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
      }
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const controls = Array.from(drawerRef.current.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),textarea,select:not(:disabled),summary'))
        .filter((element) => !element.closest('details:not([open])') || element.tagName === 'SUMMARY');
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || !drawerRef.current.contains(document.activeElement))) {
        event.preventDefault(); if (last) last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); if (first) first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <>
      <TriggerButton ref={triggerRef} type="button" $inverse={inverse} aria-haspopup="dialog" aria-expanded={open}
        onClick={() => { setOpened(true); setOpen(true); }}>
        <FiMessageSquare aria-hidden="true" />{administrationLabels(String(i18n.language || 'en').split('-')[0]).assistant}
      </TriggerButton>
      {/* Keep the session transcript when closed. The portal escapes the header stacking context. */}
      {opened && createPortal(
        <>
          {open && <Backdrop onMouseDown={close} />}
          <Drawer ref={drawerRef} hidden={!open} role="dialog" aria-modal="true" aria-label={t('assistant.title')}>
            <DrawerHeader>
              <DrawerTitle><FiMessageSquare aria-hidden="true" />{t('assistant.title')}</DrawerTitle>
              <CloseButton type="button" onClick={close} aria-label={t('assistant.close')}><FiX aria-hidden="true" /></CloseButton>
            </DrawerHeader>
            <AssistantPanel ask={aiService.askAssistant} loadSettings={aiService.getAssistantSettings}
              saveSettings={aiService.saveAssistantSettings} refreshKnowledge={aiService.refreshAssistantKnowledge}
              language={String(i18n.language || 'en').toLowerCase().split('-')[0]}
              page={window.location.pathname} labels={labels} active={open} />
          </Drawer>
        </>, document.body
      )}
    </>
  );
};

export default AskAiAssistant;
